// @spec IAM-STR-017
// @spec IAM-STR-015
// @spec IAM-STR-018
// @spec IAM-STR-019
// @spec IAM-STR-020
// @spec SHL-STR-005
'use client';

import { useUser } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, UserPlus } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import type { OrganizationMember, PendingInvitation } from '@/features/team';
import {
  ConfirmRevokeInvitationDialog,
  InviteModal,
  MembersSearch,
  MembersTable,
  PendingInvitations,
  RemoveMemberDialog,
  useMembers,
  useRemoveMember,
  useRevokeInvitation,
  useUpdateMemberRole,
} from '@/features/team';
import { useOrgRole } from '@/hooks/use-org-role';
import { useOrganizations } from '@/hooks/use-organizations';
import { useRoles } from '@/hooks/use-roles';
import i18n from '@/lib/i18n';
import { ApiClientError } from '@/types/api';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/team')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.team'),
  }),
  component: TeamSettingsPage,
});

type SortField = 'name' | 'email' | 'role' | 'joinedAt' | 'lastActiveAt';
type SortOrder = 'asc' | 'desc';

/**
 * Team settings page displaying organization members and pending invitations.
 *
 * Requires Admin or Manager role to access. Provides search, filtering,
 * sorting, and pagination for the members list.
 */
function TeamSettingsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { user } = useUser();
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const { isAdmin, isAdminOrManager } = useOrgRole();

  // Get active organization
  const activeOrg = organizations.find((org) => org.isActive);
  const canViewMembers = isAdminOrManager;
  const canInviteMembers = isAdminOrManager;
  const canChangeRoles = isAdmin;
  const canRemoveMembers = isAdmin;

  // Search and filter state
  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const [sortBy, setSortBy] = React.useState<SortField>('name');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('asc');
  const [isInviteModalOpen, setIsInviteModalOpen] = React.useState(false);
  const [updatingMemberId, setUpdatingMemberId] = React.useState<string | undefined>();
  const [memberToRemove, setMemberToRemove] = React.useState<OrganizationMember | null>(null);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = React.useState<PendingInvitation | null>(
    null
  );
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = React.useState(false);

  const pageSize = 20;

  // Build useMembers options
  const activeOrgId = activeOrg?.id;
  const { roles: fetchedRoles } = useRoles({
    orgId: activeOrgId,
    enabled: Boolean(activeOrgId) && canViewMembers,
  });
  const membersOptions = React.useMemo(() => {
    const options: Parameters<typeof useMembers>[0] = {
      orgId: activeOrgId ?? '',
      page,
      pageSize,
      sortBy,
      sortOrder,
      enabled: Boolean(activeOrgId) && canViewMembers,
    };
    if (search) {
      options.search = search;
    }
    if (roleFilter) {
      options.role = roleFilter;
    }
    return options;
  }, [activeOrgId, page, pageSize, search, roleFilter, sortBy, sortOrder, canViewMembers]);

  // Fetch members
  const { data, isLoading: membersLoading, error } = useMembers(membersOptions);
  const fallbackRoles = React.useMemo(() => {
    const dedupedRoles = new Map<
      string,
      {
        id: string;
        name: string;
        isSystemRole: boolean;
        description?: string;
      }
    >();

    for (const member of data?.members ?? []) {
      dedupedRoles.set(member.role.id, member.role);
    }

    for (const invitation of data?.pendingInvitations ?? []) {
      dedupedRoles.set(invitation.role.id, invitation.role);
    }

    return Array.from(dedupedRoles.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [data?.members, data?.pendingInvitations]);
  const availableRoles = React.useMemo(() => {
    if (fetchedRoles.length > 0) {
      return fetchedRoles.map((role) => ({
        id: role.id,
        name: role.name,
        isSystemRole: role.isSystemRole,
        ...(role.description !== null ? { description: role.description } : {}),
      }));
    }
    return fallbackRoles;
  }, [fetchedRoles, fallbackRoles]);

  // Reset to page 1 when search or filter changes
  React.useEffect(() => {
    setPage(1);
  }, [search, roleFilter]);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // Role update mutation
  const updateRoleMutation = useUpdateMemberRole({
    orgId: activeOrgId ?? '',
  });

  // Handle role change
  const handleRoleChange = React.useCallback(
    (memberId: string, newRoleId: string) => {
      setUpdatingMemberId(memberId);
      updateRoleMutation.mutate(
        { membershipId: memberId, roleId: newRoleId },
        {
          onSuccess: (data) => {
            toast.success(t('settings:toast.roleChangedFromTo', { previousRole: data.previousRole.name, newRole: data.role.name }));
            setUpdatingMemberId(undefined);
          },
          onError: (error) => {
            const message =
              error instanceof ApiClientError ? error.message : t('team.failedToUpdateRole');
            toast.error(message);
            setUpdatingMemberId(undefined);
          },
        }
      );
    },
    [updateRoleMutation]
  );

  // Member removal mutation
  const removeMemberMutation = useRemoveMember({
    orgId: activeOrgId ?? '',
  });

  // Handle remove member click (opens dialog)
  const handleRemoveClick = React.useCallback((member: OrganizationMember) => {
    setMemberToRemove(member);
    setIsRemoveDialogOpen(true);
  }, []);

  // Handle remove member confirm
  const handleRemoveConfirm = React.useCallback(() => {
    if (!memberToRemove) return;

    removeMemberMutation.mutate(memberToRemove.id, {
      onSuccess: (data) => {
        toast.success(t('settings:toast.hasBeenRemovedFromTheOrganization', { email: data.email }));
        setIsRemoveDialogOpen(false);
        setMemberToRemove(null);
      },
      onError: (error) => {
        const message = error instanceof ApiClientError ? error.message : t('team.failedToRemoveMember');
        toast.error(message);
      },
    });
  }, [memberToRemove, removeMemberMutation]);

  // Invitation revocation mutation
  const revokeInvitationMutation = useRevokeInvitation({
    orgId: activeOrgId ?? '',
  });

  // Handle revoke invitation click (opens dialog)
  const handleRevokeClick = React.useCallback(
    (invitationId: string) => {
      const pendingInvitations = data?.pendingInvitations ?? [];
      const invitation = pendingInvitations.find((inv) => inv.id === invitationId);
      if (invitation) {
        setInvitationToRevoke(invitation);
        setIsRevokeDialogOpen(true);
      }
    },
    [data?.pendingInvitations]
  );

  // Handle revoke invitation confirm
  const handleRevokeConfirm = React.useCallback(() => {
    if (!invitationToRevoke) return;

    revokeInvitationMutation.mutate(
      { invitationId: invitationToRevoke.id },
      {
        onSuccess: (data) => {
          toast.success(t('settings:toast.invitationToRevoked', { email: data.email }));
          setIsRevokeDialogOpen(false);
          setInvitationToRevoke(null);
        },
        onError: (error) => {
          const message =
            error instanceof ApiClientError ? error.message : t('team.failedToRevokeInvitation');
          toast.error(message);
        },
      }
    );
  }, [invitationToRevoke, revokeInvitationMutation]);

  const handleOpenMember = React.useCallback(
    (member: OrganizationMember) => {
      const fallbackPath = `/settings/team/members/${member.userId}`;
      try {
        const navigationResult = navigate({
          to: '/settings/team/members/$userId',
          params: { userId: member.userId },
        });
        void Promise.resolve(navigationResult).catch(() => {
          window.location.assign(fallbackPath);
        });
      } catch {
        window.location.assign(fallbackPath);
      }
    },
    [navigate]
  );

  const handleOpenMemberHistory = React.useCallback(
    (member: OrganizationMember) => {
      const fallbackPath = `/settings/team/members/${member.userId}/history-connections`;
      try {
        const navigationResult = navigate({
          to: '/settings/team/members/$userId/history-connections',
          params: { userId: member.userId },
        });
        void Promise.resolve(navigationResult).catch(() => {
          window.location.assign(fallbackPath);
        });
      } catch {
        window.location.assign(fallbackPath);
      }
    },
    [navigate]
  );
  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('team.noOrganizationSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('team.pleaseSelectOrganization')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('team.goToOrganizations')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Unauthorized - user doesn't have permission to view members
  if (!canViewMembers) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('team.accessDenied')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('team.accessDeniedDescription')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/dashboard' })}
              className="mt-4"
            >
              {t('team.goToDashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const members = data?.members ?? [];
  const invitations = data?.pendingInvitations ?? [];
  const pagination = data?.pagination;
  const totalItems = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const showingStart = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingEnd = Math.min(page * pageSize, totalItems);

  // Find current user's internal ID from members list by matching email
  const currentUserEmail = user?.primaryEmailAddress?.emailAddress;
  const currentUserMember = members.find((member) => member.email === currentUserEmail);
  const currentUserId = currentUserMember?.userId ?? '';

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('team.teamMembers')}</h1>
            <p className="mt-1 text-muted-foreground">{t('team.manageMembers', { name: activeOrg.name })}</p>
          </div>
          {canInviteMembers && (
            <Button
              onClick={() => {
                setIsInviteModalOpen(true);
              }}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              {t('team.invite')}
            </Button>
          )}
        </header>

        {/* Error state */}
        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : t('team.failedToLoadMembers')}
            </p>
          </div>
        )}

        {/* Pending Invitations */}
        <PendingInvitations
          invitations={invitations}
          isLoading={membersLoading}
          onRevoke={canInviteMembers ? handleRevokeClick : undefined}
        />

        {/* Search and filters */}
        <MembersSearch
          value={search}
          onValueChange={setSearch}
          roleFilter={roleFilter}
          onRoleFilterChange={setRoleFilter}
          roles={availableRoles}
        />

        {/* Members table */}
        <MembersTable
          members={members}
          organizationName={activeOrg.name}
          isLoading={membersLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          roles={availableRoles}
          currentUserId={currentUserId}
          onRoleChange={handleRoleChange}
          updatingMemberId={updatingMemberId}
          canChangeRoles={canChangeRoles}
          onRemove={handleRemoveClick}
          canRemoveMembers={canRemoveMembers}
          onOpenMember={handleOpenMember}
          onOpenMemberHistory={handleOpenMemberHistory}
        />

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('team.showingMembers', { start: showingStart, end: showingEnd, total: totalItems })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                }}
                disabled={page === 1 || membersLoading}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('team.previous')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('team.pageOf', { page, totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((p) => Math.min(totalPages, p + 1));
                }}
                disabled={page >= totalPages || membersLoading}
              >
                {t('team.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Invite Modal */}
        <InviteModal
          orgId={activeOrg.id}
          isOpen={isInviteModalOpen}
          onClose={() => {
            setIsInviteModalOpen(false);
          }}
          roles={availableRoles}
        />

        {/* Remove Member Dialog */}
        <RemoveMemberDialog
          open={isRemoveDialogOpen}
          onOpenChange={setIsRemoveDialogOpen}
          member={memberToRemove}
          onConfirm={handleRemoveConfirm}
          isLoading={removeMemberMutation.isPending}
        />

        {/* Revoke Invitation Dialog */}
        <ConfirmRevokeInvitationDialog
          open={isRevokeDialogOpen}
          onOpenChange={setIsRevokeDialogOpen}
          invitation={invitationToRevoke}
          onConfirm={handleRevokeConfirm}
          isLoading={revokeInvitationMutation.isPending}
        />
      </div>
    </div>
  );
}
